import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Card,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import FormProvider, { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import RHFTextArea from 'src/components/hook-form/rhf-text-area';
import { RHFUploadSingleFileAccelerator } from 'src/components/hook-form/rhf-upload';
import { useMyDataStore } from 'src/store/me/me.store';
import { useState, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

type Props = {
  methods: any;
  onSubmit: VoidFunction;
  handleDropImagePreview: (acceptedFiles: File[]) => void;
  handleDropAdditionalImages: (acceptedFiles: File[], index: number) => void;
  fields: any;
  append: (value: any) => void;
  remove: (index: number) => void;
  removeAndEnsureEmpty: (index: number) => void;
  handleDropIconImage: (acceptedFiles: File[]) => void;
  renderedLocations: { value: string; label: string }[];
  STATUS_LABEL: string[];
  STATUS_OPTIONS: string[];
  isEdit?: boolean;
  isSubmitting?: boolean;
  carouselRef: React.RefObject<HTMLDivElement>;
};

export default function CollectibleForm({
  methods,
  onSubmit,
  handleDropImagePreview,
  handleDropAdditionalImages,
  fields,
  append,
  remove,
  removeAndEnsureEmpty,
  handleDropIconImage,
  renderedLocations,
  STATUS_LABEL,
  STATUS_OPTIONS,
  isEdit,
  isSubmitting,
  carouselRef,
}: Props) {
  const permission = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits);
  const theme = useTheme();
  const hasCustomerVoice = useWatch({ control: methods.control, name: 'hasCustomerVoice' });
  const [customerVoiceEmbedScript, setCustomerVoiceEmbedScript] = useState('');
  const [isCustomerVoiceScriptDialogOpen, setIsCustomerVoiceScriptDialogOpen] = useState(false);
  const [isScriptInstructionsPreviewOpen, setIsScriptInstructionsPreviewOpen] = useState(false);
  const scriptInstructionsPreviewAnchorRef = useRef<HTMLDivElement | null>(null);

  const applyCustomerVoiceEmbedScript = (rawScript: string) => {
    const script = rawScript.trim();

    if (!script) {
      return false;
    }

    const surveyEmbedMatch = script.match(
      /new\s+SurveyEmbed\s*\(\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]/i
    );

    const formId = surveyEmbedMatch?.[1]?.trim();
    const baseUrl = surveyEmbedMatch?.[2]?.trim();
    const assetsUrl = surveyEmbedMatch?.[3]?.trim();

    if (!formId && !baseUrl && !assetsUrl) {
      return false;
    }

    methods.setValue('hasCustomerVoice', true, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (baseUrl) {
      methods.setValue('customerVoiceData.baseUrl', baseUrl, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (assetsUrl) {
      methods.setValue('customerVoiceData.assetsUrl', assetsUrl, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (formId) {
      methods.setValue('customerVoiceData.customerVoiceFormId', formId, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    return true;
  };

  const renderConditionalReveal = (condition: boolean, content: ReactNode) => (
    <Collapse in={condition} timeout={{ enter: 280, exit: 200 }} unmountOnExit>
      <Box
        sx={{
          pt: 0.5,
          opacity: condition ? 1 : 0,
          transform: condition ? 'translateY(0)' : 'translateY(-4px)',
          transition: 'opacity 220ms ease, transform 220ms ease',
        }}
      >
        {content}
      </Box>
    </Collapse>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Card>
        <Stack spacing={2} p={4}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm="auto">
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                Ícone do colecionável:
              </Typography>
              <RHFUploadSingleFileAccelerator
                name="icon"
                acceptAll={false}
                // @ts-ignore
                onDrop={handleDropIconImage}
                iconImage
              />
            </Grid>
            <Grid item xs={12} sm>
              <Stack spacing={2} mb={1}>
                <RHFTextField name="name" label="Nome do colecionável" />
                <RHFTextField name="simplifiedName" label="Nome simplificado" />
              </Stack>
            </Grid>
          </Grid>
          <RHFTextArea name="description" label="Descrição" />

          <Typography variant="body1" fontWeight="bold">
            Imagem preview do colecionável:
          </Typography>
          <RHFUploadSingleFileAccelerator
            name="imagePreview"
            acceptAll={false}
            // @ts-ignore
            onDrop={handleDropImagePreview}
          />
          <Typography variant="body1" fontWeight="bold">
            Imagens adicionais do colecionável:
          </Typography>
          <Stack
            ref={carouselRef}
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: 4 },
              paddingBottom: 2,
            }}
          >
            <Stack sx={{ display: 'flex', flexDirection: 'row' }} spacing={4} px={4}>
              {fields.map((field: any, index: number) => (
                <Stack key={field.id} alignItems="center" spacing={1}>
                  <RHFUploadSingleFileAccelerator
                    name={`additionalImages.${index}`}
                    acceptAll={false}
                    // @ts-ignore
                    onDrop={(files) => handleDropAdditionalImages(files, index)}
                    carouselImage
                    onRemove={() => removeAndEnsureEmpty(index)}
                  />
                </Stack>
              ))}
            </Stack>
          </Stack>
          <RHFAutocomplete
            sx={{ mt: 2 }}
            options={
              renderedLocations.filter(
                (op: { value: string; label: string }) => op.value !== 'all'
              ) || []
            }
            name="locations"
            label="Localização"
          />
          <RHFTextField
            name="daysToRescue"
            type="number"
            label="Quantidade de dias para a retirada"
          />
          <RHFTextField name="price" type="number" label="Preço em pontos" />
          <RHFTextField name="totalQuantity" type="number" label="Quantidade total" />
          <RHFTextField name="priority" type="number" label="Prioridade" />
          <Divider />
          <Controller
            name="hasCustomerVoice"
            control={methods.control}
            render={({ field }) => (
              <>
                <Typography variant="h6" fontWeight={700}>
                  Formulário de Feedback
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    backgroundColor: theme.palette.grey[100],
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" fontWeight={600}>
                      Customer Voice
                    </Typography>
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(_, checked) => {
                        field.onChange(checked);

                        if (checked) {
                          return;
                        }

                        methods.setValue('customerVoiceData.baseUrl', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        methods.setValue('customerVoiceData.assetsUrl', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        methods.setValue('customerVoiceData.customerVoiceFormId', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                    />
                  </Stack>

                  {renderConditionalReveal(
                    hasCustomerVoice,
                    <Stack spacing={2}>
                      <Stack spacing={2}>
                        <RHFTextField name="customerVoiceData.baseUrl" label="URL base" />
                        <RHFTextField name="customerVoiceData.assetsUrl" label="URL dos recursos" />
                        <RHFTextField
                          name="customerVoiceData.customerVoiceFormId"
                          label="ID do formulário do Customer Voice"
                        />
                      </Stack>
                      <Stack direction="row" justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          disableRipple
                          disableFocusRipple
                          disableTouchRipple
                          onClick={() => setIsCustomerVoiceScriptDialogOpen(true)}
                        >
                          Inserir script
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </Box>
              </>
            )}
          />
          <Dialog
            open={isCustomerVoiceScriptDialogOpen}
            onClose={() => setIsCustomerVoiceScriptDialogOpen(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Typography variant="h6">Colar script do Customer Voice</Typography>
                <Stack spacing={1} alignItems="flex-start">
                  <Stack direction="row" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Como conseguir o script
                    </Typography>
                    <Tooltip
                      arrow
                      placement="left"
                      title={
                        <Stack spacing={0.5} sx={{ py: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            Como obter o script (exemplo)
                          </Typography>
                          <Typography variant="caption">1. Acesse aba de inserção.</Typography>
                          <Typography variant="caption">2. Escolher formato embutido.</Typography>
                          <Typography variant="caption">
                            3. Copie o script dentro do campo de código inserido (abaixo você pode
                            ver uma imagem de exemplo).
                          </Typography>
                        </Stack>
                      }
                    >
                      <IconButton size="small" sx={{ p: 0.4 }}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box
                    ref={scriptInstructionsPreviewAnchorRef}
                    component="img"
                    src="/assets/script_instructions.png"
                    alt="Exemplo de como localizar e copiar o script"
                    onMouseEnter={() => setIsScriptInstructionsPreviewOpen(true)}
                    onMouseLeave={() => setIsScriptInstructionsPreviewOpen(false)}
                    sx={{
                      width: 160,
                      maxWidth: '100%',
                      display: 'block',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'default',
                      boxShadow: 1,
                      bgcolor: 'background.paper',
                    }}
                  />
                </Stack>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Script embedado
              </Typography>
              {typeof document !== 'undefined' && isScriptInstructionsPreviewOpen
                ? createPortal(
                    <Box
                      onMouseEnter={() => setIsScriptInstructionsPreviewOpen(true)}
                      onMouseLeave={() => setIsScriptInstructionsPreviewOpen(false)}
                      sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: (currentTheme) => currentTheme.zIndex.modal + 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <Box
                        component="img"
                        src="/assets/script_instructions.png"
                        alt="Exemplo ampliado de como localizar e copiar o script"
                        sx={{
                          width: 'min(92vw, 1200px)',
                          maxHeight: '88vh',
                          objectFit: 'contain',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: (currentTheme) => currentTheme.shadows[24],
                          backgroundColor: 'background.paper',
                          pointerEvents: 'none',
                        }}
                      />
                    </Box>,
                    document.body
                  )
                : null}
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={6}
                value={customerVoiceEmbedScript}
                onChange={(event) => {
                  const nextScript = event.target.value;
                  setCustomerVoiceEmbedScript(nextScript);

                  const hasAutoCompleted = applyCustomerVoiceEmbedScript(nextScript);

                  if (hasAutoCompleted) {
                    setIsCustomerVoiceScriptDialogOpen(false);
                  }
                }}
                placeholder="Cole aqui o script completo para extrair os dados automaticamente"
                helperText="Quando os dados forem extraídos com sucesso, este diálogo será fechado automaticamente."
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsCustomerVoiceScriptDialogOpen(false)}>Cancelar</Button>
            </DialogActions>
          </Dialog>
          <Stack>
            <Typography variant="body1" fontWeight="bold">
              Status:
            </Typography>
            <RHFRadioGroupLegacy
              name="status"
              options={STATUS_OPTIONS}
              getOptionLabel={STATUS_LABEL}
            />
          </Stack>
          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={(isEdit && !permission?.update) || (!isEdit && !permission?.create)}
            >
              {!isEdit ? 'Cadastrar Colecionável' : 'Editar Colecionável'}
            </LoadingButton>
          </Stack>
        </Stack>
      </Card>
    </FormProvider>
  );
}
